import { createId as cuid } from "@paralleldrive/cuid2"

import { logger } from "@/utils/monitoring/logging"
import { PhotoType } from "@/utils/types"

import { dexie } from "../db"
import { modifyOrderedStep } from "../tables/orderedSteps/orderedSteps"
import { findPhotoById, getPhotoBlob, upsertPhoto } from "../tables/photos/photos"
import { logDexieError } from "../utils/logDexieError"

type AddClaimPhotoCopyParams = {
  sourcePhotoId: string
  type: PhotoType
}

/**
 * Copies a saved Claim photo into the view step it already covers and finishes that step,
 * so the flow skips it. The blob is duplicated locally only, the server copy shares storageRef.
 */
export async function addClaimPhotoCopy(params: AddClaimPhotoCopyParams): Promise<void> {
  const { sourcePhotoId, type } = params

  try {
    await dexie.transaction("rw", [dexie.photos, dexie.photoBlobs, dexie.orderedSteps], async () => {
      const source = await findPhotoById(sourcePhotoId)
      const blob = await getPhotoBlob(sourcePhotoId)
      if (!source || !blob) {
        logger.error(`[claim] Cannot copy claim photo ${sourcePhotoId} to ${type}, source or blob missing`)
        return
      }

      const now = new Date()
      await upsertPhoto({
        id: cuid(),
        sessionId: source.sessionId,
        type,
        sourcePhotoId,
        data: blob.data,
        mime: blob.mime,
        thumbnailData: blob.thumbnailData,
        thumbnailMime: blob.thumbnailMime,
        createdAt: now,
        updatedAt: now,
        uploadProgress: -1,
        uploadFailed: false,
        retries: 0,
        location: source.location,
        userModelMatch: "Exact",
        carViewPredictedType: source.carViewBrowserModelResult?.carView.label,
        carViewBrowserModelResult: source.carViewBrowserModelResult,
      })

      await modifyOrderedStep(type, source.sessionId, { isFinished: true })
      logger.info(`[claim] Claim photo ${sourcePhotoId} reused for step ${type}, step skipped`)
    })
  } catch (error) {
    logDexieError(error)
  }
}
